import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useStatsigClient } from '@statsig/react-bindings';
import { useLocation } from 'react-router-dom';
import { createAssistantMessageId } from "../../utils/clientIdentity";
import { buildAssistantHeaders, buildAssistantUrl } from "../../utils/assistantApi";
import { triggerAssistantAuthLogout } from "../../utils/authSession";
import { emitAssistantToast } from "../../utils/assistantToast";
import {
    addMessage,
    userTyping,
    assistantResponding,
    setActiveRequest,
    clearActiveRequest,
    addSelectedValue,
    removeSelectedValue,
    setActiveReport,
    updateReportMessage,
    removeMessageById,
} from "../../state/actions";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import ErrorCodes from '@/constants/errors/ErrorCodes';
import HistoryMetaData from "../HistoryMetaData";
import Tooltip from "../Tooltip";
import { FiArrowUp, FiGlobe, FiFileText, FiBarChart2 } from "react-icons/fi";
import "./MessageInput.scss"

const MessageInput = ({
    isAssistantReady = true,
}) => {
    const { checkGate } = useStatsigClient();
    const location = useLocation();
    const [messageInput, setMessageInput] = useState("");
    const dispatch = useDispatch();
    const store = useStore();
    const typingTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const requestLaunchInProgressRef = useRef(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const {
        selectedValues,
        isAssistantResponding,
        activeRequestId,
    } = useSelector((state) => state.chat);
    const { clientId, conversationId } = useAssistantAbly();
    const latestConversationIdRef = useRef(conversationId || null);
    const isSummaryButtonEnabled = checkGate(AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_SUMMARISE_SCREEN_ENABLED);
    const isReportBuilderButtonEnabled = checkGate(AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_REPORT_BUILDER_ENABLED);
    const isHistoryMetadataContextEnabled = checkGate(AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_HISTORY_METADATA_CONTEXT_ENABLED)
    const summaryHiddenRoutes = AI_ASSISTANT_CONSTANTS.SUMMARY.HIDDEN_ROUTES;
    const shouldHideSummary = summaryHiddenRoutes.some((route) =>
        location?.pathname?.startsWith(route)
    );
    const shouldShowButton = isSummaryButtonEnabled && !shouldHideSummary;
    const shouldShowReportBuilderButton = isReportBuilderButtonEnabled;
    const disableNewsSearchButton = !isAssistantReady;
    const disableSummaryButton = !isAssistantReady;
    const disableReportBuilderButton =
        !isAssistantReady ||
        !isReportBuilderButtonEnabled ||
        isAssistantResponding ||
        Boolean(activeRequestId);
    const NEWS_SEARCH_VALUE = AI_ASSISTANT_CONSTANTS.NEWS_SEARCH.VALUE;
    const isNewsSearchActive = selectedValues.includes(NEWS_SEARCH_VALUE);
    const messageSelectedValues = selectedValues.filter(
        (value) => value !== NEWS_SEARCH_VALUE
    );
    const isSendDisabled =
        !isAssistantReady ||
        Boolean(activeRequestId) ||
        isAssistantResponding ||
        (messageInput.trim().length === 0 && messageSelectedValues.length === 0);
    const convertQueryParamsToObject = (searchString) => {
        const resolvedSearch = searchString ?? window.location.search;
        const urlParams = new URLSearchParams(resolvedSearch);
        const paramsWithArrays = {};
        for (const [key, value] of urlParams) {
            if (paramsWithArrays[key]) {

                paramsWithArrays[key] = Array.isArray(paramsWithArrays[key])
                    ? [...paramsWithArrays[key], value]
                    : [paramsWithArrays[key], value];
            } else {
                paramsWithArrays[key] = value;
            }
        }
        return paramsWithArrays;
    };

    const buildCurrentScreenContext = () => {
        const pathSegments = (location?.pathname || "")
            .split("/")
            .filter(Boolean);
        const module = pathSegments[0];
        const groupBy = pathSegments[1];
        const params = convertQueryParamsToObject(location?.search || "");
        delete params[AI_ASSISTANT_CONSTANTS.NEWS_SEARCH.FLAGS_KEY];
        if (module) params.module = module;
        if (groupBy) params.groupBy = groupBy;
        return params;
    };
    const createInitialReportState = (reportId) => ({
        report_id: reportId,
        title: AI_ASSISTANT_CONSTANTS.REPORT.INITIAL_TITLE,
        description: AI_ASSISTANT_CONSTANTS.REPORT.INITIAL_DESCRIPTION,
        sections: [],
        plan: null,
        status_message: AI_ASSISTANT_CONSTANTS.REPORT.INITIAL_STATUS_MESSAGE,
        build_progress: {
            currentStage: AI_ASSISTANT_CONSTANTS.REPORT.STAGES.INITIALIZING,
            startedAt: new Date().toISOString(),
            log: [],
        },
    });
    const toPlaceholderSections = (sections = []) =>
        sections
            .filter((section) => typeof section === "object" && section !== null)
            .map((section, idx) => ({
                section_id: `planned-${idx + 1}`,
                title: section.title || `Section ${idx + 1}`,
                objective: section.objective,
                focus_metrics: section.focus_metrics || [],
                visuals: section.visuals || [],
                is_placeholder: true,
                placeholder_status: "pending",
                plan_index: idx + 1,
                blocks: [],
            }));
    const pushAssistantErrorMessage = (message, preferredMessageId) => {
        const errorText =
            (typeof message === "string" && message.trim()) ||
            AI_ASSISTANT_CONSTANTS.ERRORS.UI_UNEXPECTED;
        const preferredId =
            typeof preferredMessageId === "string" && preferredMessageId.trim()
                ? preferredMessageId.trim()
                : null;
        const existingMessageForPreferredId = preferredId
            ? (store?.getState?.()?.chat?.messages || []).find(
                (msg) => msg?.message_id === preferredId,
            )
            : null;
        const errorMessageId =
            preferredId &&
            (!existingMessageForPreferredId ||
                existingMessageForPreferredId?.message_type ===
                    AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.ERROR)
                ? preferredId
                : createAssistantMessageId();
        dispatch(removeMessageById(errorMessageId));
        dispatch(
            addMessage({
                type: AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_AGENT,
                message_type: AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.ERROR,
                message_id: errorMessageId,
                text: errorText,
                payload: {
                    error: errorText,
                    text: errorText,
                    message_id: errorMessageId,
                },
            }),
        );
    };
    const pushAssistantWarningToast = (message) => {
        const warningText =
            (typeof message === "string" && message.trim()) ||
            AI_ASSISTANT_CONSTANTS.ERRORS.REQUEST_TAKING_LONGER;
        let warningTitle = AI_ASSISTANT_CONSTANTS.TOASTS.TITLES.WARNING;
        let warningDescription = warningText;

        if (warningText === AI_ASSISTANT_CONSTANTS.ERRORS.REQUEST_ALREADY_IN_PROGRESS) {
            warningTitle = AI_ASSISTANT_CONSTANTS.TOASTS.TITLES.REQUEST_IN_PROGRESS;
            warningDescription =
                AI_ASSISTANT_CONSTANTS.TOASTS.DESCRIPTIONS.REQUEST_IN_PROGRESS;
        } else if (warningText === AI_ASSISTANT_CONSTANTS.ERRORS.REQUEST_TAKING_LONGER) {
            warningTitle = AI_ASSISTANT_CONSTANTS.TOASTS.TITLES.RESPONSE_DELAYED;
            warningDescription =
                AI_ASSISTANT_CONSTANTS.TOASTS.DESCRIPTIONS.RESPONSE_DELAYED;
        } else if (warningText === AI_ASSISTANT_CONSTANTS.ERRORS.REALTIME_CONNECTION_UNSTABLE) {
            warningTitle = AI_ASSISTANT_CONSTANTS.TOASTS.TITLES.CONNECTION_UNSTABLE;
            warningDescription =
                AI_ASSISTANT_CONSTANTS.TOASTS.DESCRIPTIONS.CONNECTION_UNSTABLE;
        }

        emitAssistantToast({
            type: AI_ASSISTANT_CONSTANTS.TOASTS.TYPES.WARNING,
            title: warningTitle,
            description: warningDescription,
            compact: true,
            showClose: false,
            badge: "5s",
            duration: 5000,
        });
    };
    const resolveRequestErrorMessage = (err) => {
        if (err.origin === "backend") {
            const backendError = ErrorCodes["SERVER-E-001"];
            const code = err?.backendCode || backendError?.code || "SERVER-E-001";
            const message =
                err?.message ?? AI_ASSISTANT_CONSTANTS.ERRORS.INTERNAL_SERVER;
            return `[${code}] - ${message}`;
        }
        const uiError = ErrorCodes["UI-E-001"];
        const code = uiError?.code ?? "UI-E-001";
        const message = err?.message ?? AI_ASSISTANT_CONSTANTS.ERRORS.UI_UNEXPECTED;
        return `[${code}] - ${message}`;
    };
    const isLikelyHtmlPayload = (value) => {
        if (typeof value !== "string") return false;
        const trimmed = value.trim().toLowerCase();
        if (!trimmed) return false;
        return (
            trimmed.startsWith("<!doctype html") ||
            trimmed.startsWith("<html") ||
            trimmed.includes("<head") ||
            trimmed.includes("<body") ||
            trimmed.includes("<pre")
        );
    };
    const normalizeBackendErrorMessage = (value) => {
        if (typeof value !== "string") return null;
        const trimmed = value.trim();
        if (!trimmed) return null;
        if (isLikelyHtmlPayload(trimmed)) {
            return AI_ASSISTANT_CONSTANTS.ERRORS.INTERNAL_SERVER;
        }
        return trimmed.length > 500 ? `${trimmed.slice(0, 500)}...` : trimmed;
    };
    const extractBackendError = async (response) => {
        if (!response) {
            return {
                message: AI_ASSISTANT_CONSTANTS.ERRORS.SERVER_ERROR,
                code: null,
                shouldLogout: false,
            };
        }
        let message = null;
        let code = null;
        let shouldLogout = false;
        try {
            const contentType = response.headers?.get?.("content-type") || "";
            if (contentType.includes("application/json")) {
                const body = await response.clone().json();
                const messageFromJson =
                    body?.message ||
                    body?.error ||
                    body?.detail ||
                    body?.data?.message ||
                    (typeof body?.data === "string" ? body.data : null);
                const normalizedJsonMessage = normalizeBackendErrorMessage(
                    typeof messageFromJson === "string"
                        ? messageFromJson
                        : messageFromJson !== null && messageFromJson !== undefined
                            ? String(messageFromJson)
                            : "",
                );
                if (normalizedJsonMessage) {
                    message = normalizedJsonMessage;
                }
                const nestedPayload =
                    body?.assistant_response ||
                    body?.assistantResponse ||
                    body?.data ||
                    {};
                code =
                    body?.code ||
                    nestedPayload?.code ||
                    nestedPayload?.errorCode ||
                    null;
                shouldLogout = Boolean(
                    body?.should_logout ??
                        body?.shouldLogout ??
                        nestedPayload?.should_logout ??
                        nestedPayload?.shouldLogout,
                );
            }
            if (!message) {
                const text = await response.clone().text();
                const normalizedTextMessage = normalizeBackendErrorMessage(text);
                if (normalizedTextMessage) {
                    message = normalizedTextMessage;
                }
            }
        } catch (_parseError) {
            // Ignore parsing issues and fall back to statusText/default message.
        }
        if (!message) {
            message =
                normalizeBackendErrorMessage(response.statusText) ||
                AI_ASSISTANT_CONSTANTS.ERRORS.SERVER_ERROR;
        }
        if (!code && (response.status === 401 || response.status === 403)) {
            code = "AUTH_EXPIRED";
        }
        if (!shouldLogout && String(code || "").toUpperCase() === "AUTH_EXPIRED") {
            shouldLogout = true;
        }
        return { message, code, shouldLogout };
    };
    const shouldTriggerAuthLogout = (err) => {
        const backendCode = String(err?.backendCode || "").toUpperCase();
        if (err?.shouldLogout) return true;
        if (backendCode === "AUTH_EXPIRED") return true;
        return err?.status === 401 || err?.status === 403;
    };
    const getRequestScopedErrorId = (requestMessageId) =>
        requestMessageId ? `error-${requestMessageId}` : undefined;
    const handleRequestFailure = async (err, requestMessageId) => {
        const resolvedErrorMessage = resolveRequestErrorMessage(err);
        pushAssistantErrorMessage(
            resolvedErrorMessage,
            getRequestScopedErrorId(requestMessageId),
        );
        dispatch(clearActiveRequest());
        dispatch(assistantResponding(false));
        if (shouldTriggerAuthLogout(err)) {
            await triggerAssistantAuthLogout();
        }
    };
    const hasInFlightAssistantRequest = () => {
        const chatState = store?.getState?.()?.chat || {};
        return (
            requestLaunchInProgressRef.current ||
            Boolean(chatState?.isAssistantResponding) ||
            Boolean(chatState?.activeRequestId)
        );
    };
    const notifyConcurrentRequest = () => {
        pushAssistantWarningToast(
            AI_ASSISTANT_CONSTANTS.ERRORS.REQUEST_ALREADY_IN_PROGRESS,
        );
    };
    useEffect(() => {
        latestConversationIdRef.current = conversationId || null;
        requestLaunchInProgressRef.current = false;
    }, [conversationId]);
    useEffect(() => {
        if (!activeRequestId && !isAssistantResponding) {
            requestLaunchInProgressRef.current = false;
        }
    }, [activeRequestId, isAssistantResponding]);
    useEffect(() => {
        if (messageInput.length > 0 || isInputFocused) {
            dispatch(userTyping(true));
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                dispatch(userTyping(false));
            }, AI_ASSISTANT_CONSTANTS.INPUT.TYPING_TIMEOUT_MS);
        } else {
            dispatch(userTyping(false));
        }
        return () => clearTimeout(typingTimeoutRef.current);
    }, [messageInput, isInputFocused, dispatch]);
    useEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;
        const resizeObserver = new ResizeObserver(() => {
            adjustTextareaHeight();
        });
        resizeObserver.observe(textarea);
        return () => resizeObserver.disconnect();
    }, []);
    const resetTextareaHeight = () => {
        if (inputRef.current) {
            inputRef.current.style.height = "auto";
            inputRef.current.style.overflowY = "hidden";
        }
    };
    const streamReportBuild = async (clientReportId, requestPayload, initialReportState) => {
        let reportSnapshot = { ...initialReportState };
        let statusMessage = AI_ASSISTANT_CONSTANTS.REPORT.INITIAL_STATUS_MESSAGE;
        let buildCompleted = false;

        const pushUpdate = (overrides = {}) => {
            dispatch(updateReportMessage(clientReportId, {
                report_data: { ...reportSnapshot },
                report_status:
                    overrides.report_status ??
                    AI_ASSISTANT_CONSTANTS.REPORT.STATUS.PROCESSING,
                report_status_message: overrides.report_status_message ?? statusMessage,
                report_title: reportSnapshot.title,
            }));
        };

            const response = await fetch(
                buildAssistantUrl(AI_ASSISTANT_CONSTANTS.API.ENDPOINTS.BUILD_REPORT),
                {
                    method: "POST",
                    headers: buildAssistantHeaders(),
                    credentials: "include",
                    body: JSON.stringify({ ...requestPayload, clientReportId }),
                }
            );

        if (!response.ok) {
            const err = new Error(
                response.statusText || AI_ASSISTANT_CONSTANTS.ERRORS.SERVER_ERROR,
            );
            err.origin = "backend";
            throw err;
        }

        if (!response.body) {
            throw new Error(AI_ASSISTANT_CONSTANTS.ERRORS.STREAM_UNSUPPORTED);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        pushUpdate({ report_status_message: statusMessage });

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split("\n");
            buffer = parts.pop();
            for (const part of parts) {
                const trimmed = part.trim();
                if (!trimmed) {
                    continue;
                }
                let chunkJson;
                try {
                    chunkJson = JSON.parse(trimmed);
                } catch (error) {
                    console.warn("Invalid JSON chunk from report stream", error);
                    continue;
                }
                if (chunkJson.client_report_id && chunkJson.client_report_id !== clientReportId) {
                    continue;
                }
                const { type } = chunkJson;
                if (type === "report_status") {
                    const stage =
                        chunkJson.stage ||
                        reportSnapshot.build_progress?.currentStage ||
                        AI_ASSISTANT_CONSTANTS.REPORT.STATUS.PROCESSING;
                    statusMessage = chunkJson.message || statusMessage;
                    const logEntry = {
                        stage,
                        message: statusMessage,
                        timestamp: new Date().toISOString(),
                        detail: chunkJson.detail,
                    };
                    const prevLog = reportSnapshot.build_progress?.log || [];
                    const nextLog = [...prevLog, logEntry].slice(-25);
                    reportSnapshot = {
                        ...reportSnapshot,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: stage,
                            lastUpdate: new Date().toISOString(),
                            log: nextLog,
                            totalSections: chunkJson.total_sections ?? reportSnapshot.build_progress?.totalSections,
                            completedSections: chunkJson.completed_sections ?? reportSnapshot.build_progress?.completedSections,
                        },
                    };
                    pushUpdate({ report_status_message: statusMessage });
                } else if (type === "report_plan") {
                    const planSections = toPlaceholderSections(chunkJson.sections || []);
                    reportSnapshot = {
                        ...reportSnapshot,
                        title: chunkJson.title || reportSnapshot.title,
                        description: chunkJson.description || reportSnapshot.description,
                        plan: {
                            title: chunkJson.title,
                            description: chunkJson.description,
                            sections: chunkJson.sections || [],
                        },
                        sections: planSections,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            totalSections: planSections.length || reportSnapshot.build_progress?.totalSections,
                        },
                    };
                    pushUpdate();
                } else if (type === "report_section") {
                    const sectionData = chunkJson.section;
                    if (!sectionData) {
                        continue;
                    }
                    const targetIndex = Math.max((chunkJson.index || 1) - 1, 0);
                    const existingSections = reportSnapshot.sections ? [...reportSnapshot.sections] : [];
                    if (targetIndex >= existingSections.length) {
                        existingSections[targetIndex] = { ...sectionData, is_placeholder: false };
                    } else {
                        existingSections[targetIndex] = { ...sectionData, is_placeholder: false };
                    }
                    const completed = chunkJson.completed_sections ?? reportSnapshot.build_progress?.completedSections ?? targetIndex + 1;
                    const total = chunkJson.total_sections ?? reportSnapshot.build_progress?.totalSections ?? existingSections.length;
                    statusMessage = `Section ${completed}/${total} finalized`;
                    reportSnapshot = {
                        ...reportSnapshot,
                        sections: existingSections,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            completedSections: completed,
                            totalSections: total,
                            currentStage: AI_ASSISTANT_CONSTANTS.REPORT.STAGES.SECTION_READY,
                        },
                    };
                    pushUpdate({ report_status_message: statusMessage });
                } else if (type === "report_complete") {
                    const finalReport = chunkJson.report || {};
                    const finalReportId = finalReport.report_id || clientReportId;
                    const mergedReport = {
                        ...finalReport,
                        plan: reportSnapshot.plan,
                        status_message: AI_ASSISTANT_CONSTANTS.REPORT.READY_MESSAGE,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: AI_ASSISTANT_CONSTANTS.REPORT.STAGES.COMPLETED,
                            completedAt: new Date().toISOString(),
                            log: [
                                ...(reportSnapshot.build_progress?.log || []),
                                {
                                    stage: AI_ASSISTANT_CONSTANTS.REPORT.STAGES.COMPLETED,
                                    message: AI_ASSISTANT_CONSTANTS.REPORT.READY_MESSAGE,
                                    timestamp: new Date().toISOString(),
                                },
                            ].slice(-25),
                        },
                    };
                    reportSnapshot = mergedReport;
                    dispatch(updateReportMessage(clientReportId, {
                        report_id: finalReportId,
                        report_status: AI_ASSISTANT_CONSTANTS.REPORT.STATUS.SUCCESS,
                        report_status_message: AI_ASSISTANT_CONSTANTS.REPORT.READY_MESSAGE,
                        report_title: mergedReport.title || AI_ASSISTANT_CONSTANTS.REPORT.DEFAULT_TITLE,
                        report_data: mergedReport,
                    }));
                    dispatch(setActiveReport(finalReportId));
                    buildCompleted = true;
                    return finalReportId;
                } else if (type === "report_error") {
                    const errorMessage =
                        chunkJson?.error?.message ||
                        chunkJson?.message ||
                        AI_ASSISTANT_CONSTANTS.REPORT.ERROR_FALLBACK;
                    reportSnapshot = {
                        ...reportSnapshot,
                        error: chunkJson.error || { message: errorMessage },
                        status_message: errorMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: AI_ASSISTANT_CONSTANTS.REPORT.STAGES.ERROR,
                            log: [
                                ...(reportSnapshot.build_progress?.log || []),
                                {
                                    stage: AI_ASSISTANT_CONSTANTS.REPORT.STAGES.ERROR,
                                    message: errorMessage,
                                    timestamp: new Date().toISOString(),
                                },
                            ].slice(-25),
                        },
                    };
                    dispatch(updateReportMessage(clientReportId, {
                        report_status: AI_ASSISTANT_CONSTANTS.REPORT.STATUS.FAILURE,
                        report_status_message: errorMessage,
                        report_title: reportSnapshot.title,
                        report_data: reportSnapshot,
                    }));
                    buildCompleted = true;
                    return null;
                }
            }
        }

        if (!buildCompleted) {
            throw new Error(AI_ASSISTANT_CONSTANTS.REPORT.STREAM_END_ERROR);
        }

    };
    const handleBuildReportClick = async () => {
        if (!isAssistantReady) {
            return;
        }
        if (hasInFlightAssistantRequest()) {
            notifyConcurrentRequest();
            return;
        }
        const report_id = `report-${Date.now()}`;
        const initialState = createInitialReportState(report_id);
        const initialReportMessage = {
            type: AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_AGENT,
            message_type: AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.REPORT,
            report_id: report_id,
            report_data: initialState,
            report_status: AI_ASSISTANT_CONSTANTS.REPORT.STATUS.PROCESSING,
            report_status_message: AI_ASSISTANT_CONSTANTS.REPORT.INITIAL_STATUS_MESSAGE,
            report_title: AI_ASSISTANT_CONSTANTS.REPORT.BUILD_TITLE,
            payload: {
                report_id: report_id,
                report_data: initialState,
                report_status: AI_ASSISTANT_CONSTANTS.REPORT.STATUS.PROCESSING,
                report_status_message: AI_ASSISTANT_CONSTANTS.REPORT.INITIAL_STATUS_MESSAGE,
                report_title: AI_ASSISTANT_CONSTANTS.REPORT.BUILD_TITLE,
            }
        };
        dispatch(addMessage(initialReportMessage));
        dispatch(setActiveReport(report_id));

        try {
            const data = convertQueryParamsToObject(location?.search || "");
            const page = location.pathname;
            const payload = { data, page };
            await streamReportBuild(report_id, payload, initialState);
        } catch (error) {
            console.error("Build report error:", error);
            const updatedData = {
                report_status: AI_ASSISTANT_CONSTANTS.REPORT.STATUS.FAILURE,
                report_status_message:
                    error?.message || AI_ASSISTANT_CONSTANTS.REPORT.FAILED_TITLE,
                report_title: AI_ASSISTANT_CONSTANTS.REPORT.FAILED_TITLE,
                report_data: {
                    ...initialState,
                    status_message:
                        error?.message || AI_ASSISTANT_CONSTANTS.REPORT.FAILED_TITLE,
                    error: {
                        message:
                            error?.message || AI_ASSISTANT_CONSTANTS.REPORT.FAILED_TITLE,
                    },
                },
            };
            dispatch(updateReportMessage(report_id, updatedData));
        }
    };
    const submitMessageButtonClickHandler = async () => {
        let requestMessageId = null;
        const requestConversationId = latestConversationIdRef.current;
        try {
            if (!isAssistantReady) return;
            if (!messageInput.trim() && messageSelectedValues.length === 0) return;
            if (hasInFlightAssistantRequest()) {
                notifyConcurrentRequest();
                return;
            }
            requestLaunchInProgressRef.current = true;
            const selectedValuesString = messageSelectedValues.join(", ");
            const combinedMessage =
                selectedValuesString.length > 0
                    ? `${messageInput} [${selectedValuesString}]`
                    : messageInput;
            requestMessageId = createAssistantMessageId();
            dispatch(addMessage({
                type: AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_SENDER,
                message_type: AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.TEXT,
                message_id: requestMessageId,
                text: combinedMessage,
                payload: { text: combinedMessage, message_id: requestMessageId },
            }));
            dispatch(setActiveRequest(requestMessageId));
            messageSelectedValues.forEach((value) => dispatch(removeSelectedValue(value)));
            setMessageInput("");
            resetTextareaHeight();
            setIsInputFocused(false);
            if (inputRef.current) {
                inputRef.current.blur();
            }
            dispatch(assistantResponding(true));
            await sendAssistantRequest(combinedMessage, requestMessageId);
        } catch (err) {
            if (requestConversationId !== latestConversationIdRef.current) {
                return;
            }
            console.error("API Error:", err);
            await handleRequestFailure(err, requestMessageId);
        } finally {
            const currentActiveRequestId =
                store?.getState?.()?.chat?.activeRequestId || null;
            if (
                !currentActiveRequestId ||
                (requestMessageId && currentActiveRequestId === requestMessageId)
            ) {
                requestLaunchInProgressRef.current = false;
            }
        }
    };
    const sendAssistantRequest = async (userInputMessage, messageId) => {
        try {
            const page = location.pathname;
            if (!clientId || !conversationId) {
                const err = new Error(AI_ASSISTANT_CONSTANTS.ERRORS.SESSION_UNAVAILABLE);
                err.origin = "backend";
                throw err;
            }
            const currentScreenContext = buildCurrentScreenContext();
            const payload = {
                page,
                userInputMessage,
                clientId,
                conversationId,
                messageId,
                currentScreenContext,
                flags: {
                    [AI_ASSISTANT_CONSTANTS.NEWS_SEARCH.FLAGS_KEY]: isNewsSearchActive,
                },
            };
            const response = await fetch(
                buildAssistantUrl(AI_ASSISTANT_CONSTANTS.API.ENDPOINTS.USER_MESSAGE),
                {
                    method: "POST",
                    headers: buildAssistantHeaders({
                        [AI_ASSISTANT_CONSTANTS.API.HEADERS.CLIENT_ID]: clientId,
                    }),
                    credentials: "include",
                    body: JSON.stringify(payload),
                }
            );
            if (!response.ok) {
                const backendError = await extractBackendError(response);
                const err = new Error(
                    backendError?.message || AI_ASSISTANT_CONSTANTS.ERRORS.SERVER_ERROR,
                );
                err.origin = 'backend';
                err.status = response.status;
                err.backendCode = backendError?.code || null;
                err.shouldLogout = Boolean(backendError?.shouldLogout);
                throw err;
            }
            return response;
        } catch (err) {
            err.origin ??= 'ui';
            throw err;
        }
    };
    const messageChangeHandler = (e) => {
        setMessageInput(e.target.value);
        adjustTextareaHeight();
    };
    const handleFocus = () => setIsInputFocused(true);
    const handleKeyPress = (event) => {
        if (
            event.key === "Enter" &&
            messageInput.trim() !== "" &&
            isAssistantReady &&
            !isAssistantResponding &&
            !activeRequestId
        ) {
            event.preventDefault();
            submitMessageButtonClickHandler();
        }
    };
    const toggleCheckbox = (value) => {
        if (selectedValues.includes(value)) {
            dispatch(removeSelectedValue(value));
        } else {
            dispatch(addSelectedValue(value));
        }
    };
    const toggleNewsSearch = () => toggleCheckbox(NEWS_SEARCH_VALUE);
    const adjustTextareaHeight = () => {
        const textarea = inputRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        const maxHeight = AI_ASSISTANT_CONSTANTS.INPUT.MAX_TEXTAREA_HEIGHT;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY =
            textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    };
    const getPageSummary = async () => {
        let requestMessageId = null;
        const requestConversationId = latestConversationIdRef.current;
        try {
            if (!isAssistantReady) return;
            if (hasInFlightAssistantRequest()) {
                notifyConcurrentRequest();
                return;
            }
            requestLaunchInProgressRef.current = true;
            const userInputMessage = AI_ASSISTANT_CONSTANTS.SUMMARY.PROMPT;
            requestMessageId = createAssistantMessageId();
            dispatch(addMessage({
                type: AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_SENDER,
                message_type: AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.TEXT,
                message_id: requestMessageId,
                text: userInputMessage,
                payload: { text: userInputMessage, message_id: requestMessageId },
            }));
            dispatch(setActiveRequest(requestMessageId));
            dispatch(assistantResponding(true));
            await sendAssistantRequest(userInputMessage, requestMessageId);
        } catch (err) {
            if (requestConversationId !== latestConversationIdRef.current) {
                return;
            }
            await handleRequestFailure(err, requestMessageId);
        } finally {
            const currentActiveRequestId =
                store?.getState?.()?.chat?.activeRequestId || null;
            if (
                !currentActiveRequestId ||
                (requestMessageId && currentActiveRequestId === requestMessageId)
            ) {
                requestLaunchInProgressRef.current = false;
            }
        }
    }
    return (
        <form className="card-actions" onSubmit={(e) => e.preventDefault()}>
            <div className="card-footer-wrapper">
                {isHistoryMetadataContextEnabled && isInputFocused && <HistoryMetaData />}
                <div className="card-footer-container">
                    <div className="card-footer-input-container">
                        <textarea
                            className="textarea"
                            placeholder={AI_ASSISTANT_CONSTANTS.INPUT.PLACEHOLDER}
                            onKeyPress={handleKeyPress}
                            ref={inputRef}
                            onChange={messageChangeHandler}
                            value={messageInput}
                            onFocus={handleFocus}
                            rows={1}
                            disabled={!isAssistantReady}
                        />
                        <button
                            className="submit-btn"
                            type="button"
                            onClick={submitMessageButtonClickHandler}
                            aria-label={AI_ASSISTANT_CONSTANTS.INPUT.SEND_ARIA_LABEL}
                            disabled={isSendDisabled}
                        >
                            <FiArrowUp />
                        </button>
                    </div>
                    <div className="card-footer-actions">
                        <div className="card-footer-actions-left">
                            <Tooltip
                                content={
                                    isNewsSearchActive
                                        ? AI_ASSISTANT_CONSTANTS.NEWS_SEARCH.TOOLTIP_DISABLE
                                        : AI_ASSISTANT_CONSTANTS.NEWS_SEARCH.TOOLTIP_ENABLE
                                }
                                placement="bottom"
                            >
                                <span>
                                    <button
                                        type="button"
                                        className={`ghost-circle news-search-toggle ${isNewsSearchActive ? "is-active" : ""}`}
                                        disabled={disableNewsSearchButton}
                                        onClick={disableNewsSearchButton ? undefined : toggleNewsSearch}
                                        aria-disabled={disableNewsSearchButton}
                                        aria-pressed={isNewsSearchActive}
                                        aria-label={AI_ASSISTANT_CONSTANTS.NEWS_SEARCH.TOGGLE_ARIA_LABEL}
                                    >
                                        <FiGlobe />
                                    </button>
                                </span>
                            </Tooltip>
                             {shouldShowButton && (
                                <div
                                    onClick={disableSummaryButton ? undefined : getPageSummary}
                                    className="ai-action-button"
                                    aria-disabled={disableSummaryButton}
                                    style={disableSummaryButton ? { pointerEvents: "none", opacity: 0.5 } : undefined}
                                >
                                    <FiFileText className="ai-action-icon" />
                                    {AI_ASSISTANT_CONSTANTS.SUMMARY.ACTION_LABEL}
                                </div>
                            )}
                           
                        </div>
                        <div className="card-footer-actions-right">
                           
                            {shouldShowReportBuilderButton && (
                                <div
                                    className="ai-action-button"
                                    onClick={disableReportBuilderButton ? undefined : handleBuildReportClick}
                                    aria-disabled={disableReportBuilderButton}
                                    style={disableReportBuilderButton ? { pointerEvents: "none", opacity: 0.5 } : undefined}
                                >
                                    <FiBarChart2 className="ai-action-icon" />
                                    {AI_ASSISTANT_CONSTANTS.REPORT.ACTION_LABEL}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="ai-disclaimer" role="note">
                    <div className="ai-disclaimer-text">
                        {AI_ASSISTANT_CONSTANTS.DISCLAIMER}
                    </div>
                </div>
            </div>
        </form>
    );
};
export default MessageInput;
